package com.siriusxm.pia.views.unifiedaggregator.management

import androidx.compose.runtime.Composable
import com.siriusxm.pia.components.MessageType
import com.siriusxm.pia.components.confirmationDialogWithContent
import com.siriusxm.pia.components.messageBox
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text

/**
 * A confirmation dialog for disabling a producer or consumer.
 * Requires the user to type the entity ID to confirm the action.
 *
 * @param entityType The type of entity being disabled ("producer" or "consumer")
 * @param entityName The display name of the entity to disable
 * @param entityId The ID of the entity (used as confirmation text)
 * @param onConfirm Callback invoked with true if confirmed, false if cancelled
 */
@Composable
fun DisableConfirmationDialog(
    entityType: String,
    entityName: String?,
    entityId: String,
    onConfirm: suspend (Boolean) -> Unit
) {
    confirmationDialogWithContent(
        title = "Disable ${entityType.replaceFirstChar { it.uppercase() }}",
        action = "Disable",
        confirmationText = entityId,
        confirmationInstruction = "Type '$entityId' to confirm disabling this $entityType",
        cb = onConfirm
    ) { _, _ ->
        Div({
            style {
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(12.px)
            }
        }) {
            messageBox(MessageType.WARNING) {
                Text("Are you sure you want to disable ${entityName ?: entityId}?")
            }

            Div({
                style {
                    fontSize(0.9.em)
                }
            }) {
                Text("Disabling this $entityType will remove it from the default list view and prevent it from submitting or receiving data.")
            }
        }
    }
}

/**
 * A confirmation dialog for enabling a disabled producer or consumer.
 * This is a simpler dialog that doesn't require typing confirmation text.
 *
 * @param entityType The type of entity being enabled ("producer" or "consumer")
 * @param entityName The display name of the entity to enable
 * @param onConfirm Callback invoked with true if confirmed, false if cancelled
 */
@Composable
fun EnableConfirmationDialog(
    entityType: String,
    entityName: String?,
    onConfirm: suspend (Boolean) -> Unit
) {
    confirmationDialogWithContent(
        title = "Enable ${entityType.replaceFirstChar { it.uppercase() }}",
        action = "Enable",
        confirmationText = null,
        cb = onConfirm
    ) { _, _ ->
        Div({
            style {
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(12.px)
            }
        }) {
            Text("Are you sure you want to enable ${entityName ?: "this $entityType"}?")

            Div({
                style {
                    fontSize(0.9.em)
                }
            }) {
                Text("Enabling this $entityType will restore it to the default list view and allow it to submit or receive data.")
            }
        }
    }
}
